// Step 1 of the client-side X12 pipeline: turn raw EDI text into a flat array
// of segments. Nothing here understands what an ISA, GS, ST or PO1 *means* —
// that's the dictionary (lib/dictionary) and the loop structuring in
// lib/envelope.ts / lib/parser850.ts. This module only knows how to find the
// delimiters and split on them.
//
// The ISA segment has exactly 16 elements (ISA01-ISA16) separated by a
// single-character element separator, which X12 requires. Real-world trading
// partners don't always pad ISA01-ISA15 to their spec widths, so instead of
// reading the sub-element separator (ISA16) and segment terminator off fixed
// character offsets, this scans for the 15th element separator after the
// one that opens the segment — the characters right after it are ISA16 and
// the segment terminator, however wide ISA01-ISA15 actually were.

/** A single X12 element's value — a plain string, or an array of
 * sub-elements when the element uses the component (sub-element) separator. */
export type EdiElementValue = string | string[];

export interface EdiSegment {
  /** The segment id, e.g. "ISA", "GS", "ST", "BEG", "PO1". */
  id: string;
  /** One entry per element after the id, in order (ISA01, ISA02, ... or
   * BEG01, BEG02, ...). 1-indexed access is the caller's job. */
  elements: EdiElementValue[];
}

export interface EdiDelimiters {
  /** Separates elements within a segment. Almost always "*". */
  element: string;
  /** Separates sub-elements (components) within an element. Read from ISA16. */
  subElement: string;
  /** Terminates a segment. Almost always "~". */
  segmentTerminator: string;
}

export interface TokenizeSuccess {
  ok: true;
  delimiters: EdiDelimiters;
  segments: EdiSegment[];
}

export interface TokenizeFailure {
  ok: false;
  error: string;
}

export type TokenizeResult = TokenizeSuccess | TokenizeFailure;

/** 0-indexed position of the element separator — the character immediately
 * following the literal "ISA". */
const ELEMENT_SEP_INDEX = 3;
/** Number of element separators between ISA01 and ISA16 — ISA has 16
 * elements (ISA01-ISA16), so counting 15 separators after the one at
 * ELEMENT_SEP_INDEX lands right before ISA16. */
const ISA_ELEMENT_SEPARATOR_COUNT = 15;
/** Upper bound, in characters past the element separator, within which the
 * 15 ISA element separators must be found. Generous enough to tolerate
 * unpadded real-world ISA01-ISA15 values, but short enough that a truncated
 * ISA segment can't keep counting separators that actually belong to later
 * segments (GS, ST, ...) sharing the same element separator character. */
const MAX_ISA_SEGMENT_SEARCH_WINDOW = 160;

/** Characters that commonly serve as an X12 segment terminator. Used only
 * to bound the ISA separator scan — if one of these shows up before 15
 * element separators have been found, it almost certainly belongs to a
 * later segment (the real ISA terminator), not to ISA01-ISA15 padding, so
 * the scan must not keep counting separators past it. */
const PLAUSIBLE_SEGMENT_TERMINATORS = ['~', '\r', '|', '\x1c'];

/** Finds the earliest index, at or after `fromIndex`, of any character
 * commonly used as a segment terminator, other than `elementSep` (which
 * some real-world documents use as their element separator, e.g. `|`, so
 * it must not be treated as a terminator candidate). Returns -1 if none
 * occur. */
function findFirstPlausibleTerminatorIndex(
  text: string,
  fromIndex: number,
  elementSep: string
): number {
  let earliest = -1;
  for (const candidate of PLAUSIBLE_SEGMENT_TERMINATORS) {
    if (candidate === elementSep) continue;
    const foundAt = text.indexOf(candidate, fromIndex);
    if (foundAt !== -1 && (earliest === -1 || foundAt < earliest)) {
      earliest = foundAt;
    }
  }
  return earliest;
}

function stripLeadingLineBreaks(value: string): string {
  return value.replace(/^[\r\n]+/, '');
}

function stripTrailingLineBreaks(value: string): string {
  return value.replace(/[\r\n]+$/, '');
}

/**
 * Parses raw EDI X12 text into delimiters plus a flat array of segments.
 * Never throws — a document that can't be tokenized (empty, not an ISA
 * envelope, or a truncated ISA) comes back as a typed failure so a bad
 * paste/upload can be shown as an inline error instead of blanking the page.
 */
export function tokenize(raw: string): TokenizeResult {
  if (typeof raw !== 'string' || raw.trim().length === 0) {
    return {ok: false, error: 'Document is empty.'};
  }

  // Tolerate a BOM or blank lines before the interchange begins; everything
  // downstream is indexed off this trimmed text.
  const text = raw.replace(/^\uFEFF/, '').trimStart();

  if (!text.startsWith('ISA')) {
    return {
      ok: false,
      error:
        'Document does not start with an ISA segment — not an X12 interchange.',
    };
  }

  const elementSep = text[ELEMENT_SEP_INDEX];
  if (!elementSep) {
    return {
      ok: false,
      error: 'Could not detect delimiters from the ISA segment.',
    };
  }

  const truncatedIsaError: TokenizeFailure = {
    ok: false,
    error:
      'ISA segment is truncated — expected 16 ISA elements before the segment terminator.',
  };

  const lineBreakIndex = text.indexOf('\n', ELEMENT_SEP_INDEX + 1);
  const terminatorCandidateIndex = findFirstPlausibleTerminatorIndex(
    text,
    ELEMENT_SEP_INDEX + 1,
    elementSep
  );
  const isaSearchBound = Math.min(
    lineBreakIndex === -1 ? text.length : lineBreakIndex,
    terminatorCandidateIndex === -1 ? text.length : terminatorCandidateIndex,
    ELEMENT_SEP_INDEX + 1 + MAX_ISA_SEGMENT_SEARCH_WINDOW
  );

  let searchFrom = ELEMENT_SEP_INDEX + 1;
  let subElementSepIndex = -1;
  for (let i = 0; i < ISA_ELEMENT_SEPARATOR_COUNT; i++) {
    const foundAt = text.indexOf(elementSep, searchFrom);
    if (foundAt === -1 || foundAt >= isaSearchBound) {
      return truncatedIsaError;
    }
    subElementSepIndex = foundAt + 1;
    searchFrom = foundAt + 1;
  }

  const segmentTerminatorIndex = subElementSepIndex + 1;
  const delimiters: EdiDelimiters = {
    element: elementSep,
    subElement: text[subElementSepIndex],
    segmentTerminator: text[segmentTerminatorIndex],
  };

  if (!delimiters.subElement || !delimiters.segmentTerminator) {
    return truncatedIsaError;
  }

  const rawPieces = text.split(delimiters.segmentTerminator);
  const segments: EdiSegment[] = [];

  for (const piece of rawPieces) {
    const cleaned = stripTrailingLineBreaks(stripLeadingLineBreaks(piece));
    if (cleaned.trim() === '') continue;

    const parts = cleaned.split(delimiters.element);
    const id = parts[0];
    const elements: EdiElementValue[] = parts
      .slice(1)
      .map(el =>
        el.includes(delimiters.subElement)
          ? el.split(delimiters.subElement)
          : el
      );
    segments.push({id, elements});
  }

  if (segments.length === 0) {
    return {ok: false, error: 'No segments found after the ISA envelope.'};
  }

  return {ok: true, delimiters, segments};
}
